import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Info, CheckCircle2, AlertTriangle, Wrench } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Site-wide announcement banner — renders every active, non-dismissed
 * announcement from `GET /api/announcements` in a small ribbon above the
 * header. Dismissals persist per-user (when signed in) so the banner never
 * follows them around forever. For anonymous visitors we cache dismissal
 * ids in localStorage so a refresh doesn't resurrect what they already
 * closed.
 *
 * Kinds map to color tokens so admins can pick the tone without touching CSS:
 *   info        — muted background · primary accent
 *   success     — primary tinted
 *   warning     — amber tinted
 *   maintenance — red tinted
 */
const KIND_STYLES = {
  info:        { bg: "bg-muted/70 border-border text-foreground",                icon: Info },
  success:     { bg: "bg-primary/10 border-primary/30 text-primary",             icon: CheckCircle2 },
  warning:     { bg: "bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300", icon: AlertTriangle },
  maintenance: { bg: "bg-red-500/10 border-red-500/30 text-red-700 dark:text-red-300",         icon: Wrench },
};

const LOCAL_KEY = "dpp_dismissed_announcements";

function loadLocalDismissed() {
  try { return JSON.parse(localStorage.getItem(LOCAL_KEY) || "[]"); }
  catch { return []; }
}
function pushLocalDismissed(id) {
  const cur = loadLocalDismissed();
  if (!cur.includes(id)) {
    cur.push(id);
    try { localStorage.setItem(LOCAL_KEY, JSON.stringify(cur.slice(-50))); } catch {}
  }
}

export default function SiteAnnouncementBanner() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [locallyDismissed, setLocallyDismissed] = useState(() => loadLocalDismissed());

  // Refetch when auth state changes so dismissals sync between anon → signed in.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/announcements");
        if (!cancelled) setItems(r.data.items || []);
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const visible = items.filter((a) => !locallyDismissed.includes(a.id));
  if (visible.length === 0) return null;

  const dismiss = async (id) => {
    setLocallyDismissed((cur) => [...cur, id]);
    pushLocalDismissed(id);
    if (user) {
      try { await api.post(`/announcements/${id}/dismiss`); } catch {}
    }
  };

  return (
    <div className="w-full" data-testid="site-announcement-banner">
      {visible.map((a) => {
        const style = KIND_STYLES[a.kind] || KIND_STYLES.info;
        const Icon = style.icon;
        return (
          <div
            key={a.id}
            className={`border-b ${style.bg} px-4 sm:px-6 py-2.5`}
            data-testid={`announcement-${a.id}`}
          >
            <div className="max-w-7xl mx-auto flex items-start gap-3">
              <Icon className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0 text-sm">
                <span className="font-medium">{a.title}</span>
                {a.body && <span className="ml-2 text-foreground/80">{a.body}</span>}
                {a.href && (
                  <Link
                    to={a.href.startsWith("http") ? "#" : a.href}
                    onClick={(e) => {
                      if (a.href.startsWith("http")) {
                        e.preventDefault();
                        window.open(a.href, "_blank", "noopener,noreferrer");
                      }
                    }}
                    className="ml-3 mono text-[11px] uppercase tracking-widest underline underline-offset-2 hover:no-underline"
                  >
                    {a.cta_label || "Learn more →"}
                  </Link>
                )}
              </div>
              {a.dismissible && (
                <button
                  type="button"
                  onClick={() => dismiss(a.id)}
                  data-testid={`announcement-dismiss-${a.id}`}
                  aria-label="Dismiss announcement"
                  className="opacity-70 hover:opacity-100 transition-opacity shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
